import React from 'react';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '@components/Button';
import type { SocialAccount } from '@/types/social';
import { NETWORK_LABELS } from '@/types/social';
import { formatDateTimeBR } from '@/utils/formatters';
import { accountLabel, formatCount } from '@/utils/social';
import { AccountAvatar } from './ProfileSwitcher';
import { NetworkIcon } from './SocialBits';

interface ProfileHeaderProps {
  account: SocialAccount;
  planned: number;
  feed: number;
  /** Atualizar agora (perfil + feed); só para quem administra as contas. */
  onSync?: () => void;
  syncing?: boolean;
}

/**
 * Cabeçalho da Grade como o perfil aparece na rede: foto, números, nome, bio e link.
 * Os dados vêm de "Ler feed" / leitura automática (a cada 6 h). Documentação: src/docs/redes-sociais/grade.md.
 */
export const ProfileHeader: React.FC<ProfileHeaderProps> = ({ account, planned, feed, onSync, syncing }) => {
  const posts = account.network === 'pinterest' ? 'pins' : 'publicações';
  const stats: [string, number | undefined][] = [
    [posts, account.media_count],
    ['seguidores', account.followers_count],
    ['seguindo', account.follows_count],
  ];
  const shown = stats.filter(([, v]) => v !== undefined && v !== null);
  return (
    <div className="space-y-3 mb-4">
      <div className="flex items-center gap-4 sm:gap-6">
        <AccountAvatar account={account} size="lg" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-lg font-semibold text-slate-800 truncate">{accountLabel(account)}</p>
            <span className="inline-flex items-center gap-1 text-xs text-slate-500">
              <NetworkIcon network={account.network} size={12} /> {NETWORK_LABELS[account.network]}
            </span>
            {account.profile_url && (
              <a href={account.profile_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-emerald-700" title="Abrir na rede">
                <ExternalLink size={14} />
              </a>
            )}
          </div>
          {shown.length > 0 && (
            <div className="hidden sm:flex gap-6 text-sm text-slate-700">
              {shown.map(([label, v]) => (
                <span key={label}>
                  <strong>{formatCount(v)}</strong> {label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {shown.length > 0 && (
        <div className="grid grid-cols-3 sm:hidden text-center text-xs text-slate-600 border-y border-slate-200 py-2">
          {shown.map(([label, v]) => (
            <span key={label}>
              <strong className="block text-sm text-slate-800">{formatCount(v)}</strong>
              {label}
            </span>
          ))}
        </div>
      )}
      <div className="text-sm text-slate-700 space-y-0.5">
        {account.display_name && account.network !== 'facebook' && <p className="font-semibold">{account.display_name}</p>}
        {account.biography && <p className="whitespace-pre-line [overflow-wrap:anywhere]">{account.biography}</p>}
        {account.website && (
          <a href={account.website} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline [overflow-wrap:anywhere]">
            {account.website.replace(/^https?:\/\//, '')}
          </a>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
        <span>
          {planned} planejado(s) · {feed} já publicado(s) no cache
          {account.profile_synced_at ? ` · perfil atualizado em ${formatDateTimeBR(account.profile_synced_at)}` : ' · perfil ainda não lido da rede'}
        </span>
        {onSync && account.status === 'connected' && (
          <Button size="sm" variant="outline" icon={<RefreshCw />} loading={syncing} onClick={onSync}>
            Atualizar
          </Button>
        )}
      </div>
    </div>
  );
};
